import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  get,
  getPhoenixApiTenantUrl,
  modify,
} from '@pflegenavi/shared-frontend/platform';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type { CreateLineItemConfigurationDto } from '@pflegenavi/service-provider-shared/api-endpoints';

export interface CountryResult {
  id: string;
  name: string;
  full_name: string;
  iso_code: string;
  iso_code_3: string;
  numeric_code: number;
}

export interface TaxCodeResult {
  id: string;
  display_explanation: boolean;
  explanation_short?: string;
  explanation_long?: string;
  tax_abbreviation: string;
  tax_amount: string;
  tax_name: string;
  paragraph?: string;
}

interface LineItemConfigurationResult {
  id: string;
  global_service_provider_id: string;
  amount: number;
  name: string;
  taxes: Array<TaxCodeResult & { country_id: string }>;
}

interface CreateLineItemBody {
  name: string;
  amount: number;
  taxes?: string[];
}

export interface GetLineItemConfigurationsResult {
  data: LineItemConfigurationResult[];
}

export interface GetAllCountriesResult {
  data: CountryResult[];
}

export interface GetTaxCodesByCountryResult {
  data: Array<TaxCodeResult & { country: CountryResult }>;
}

export function transformCreateBody(
  body: CreateLineItemConfigurationDto
): CreateLineItemBody {
  return {
    name: body.name,
    amount: body.amount,
    taxes: body.taxCodes,
  };
}

export interface ILineItemTaxPhoenixApi extends Api {
  getTaxCodesByCountry(dataIn: {
    params: {
      countryId: string;
    };
  }): Promise<GetTaxCodesByCountryResult>;

  getAllCountries(dataIn: void): Promise<GetAllCountriesResult>;

  getLineItemConfigurations(): Promise<GetLineItemConfigurationsResult>;

  createLineItemConfiguration(dataIn: {
    body: CreateLineItemBody;
  }): Promise<LineItemConfigurationResult>;

  deleteLineItemConfiguration(dataIn: {
    params: {
      id: string;
    };
  }): Promise<{ status: 'OK' }>;
}

export class LineItemTaxPhoenixApi implements ILineItemTaxPhoenixApi {
  getTaxCodesByCountry: ILineItemTaxPhoenixApi['getTaxCodesByCountry'];
  getAllCountries: ILineItemTaxPhoenixApi['getAllCountries'];
  getLineItemConfigurations: ILineItemTaxPhoenixApi['getLineItemConfigurations'];
  createLineItemConfiguration: ILineItemTaxPhoenixApi['createLineItemConfiguration'];
  deleteLineItemConfiguration: ILineItemTaxPhoenixApi['deleteLineItemConfiguration'];

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    const baseUrl = getPhoenixApiTenantUrl('service-provider', apiUrl);

    this.getTaxCodesByCountry = get({
      authContext,
      url: (params) => {
        return `${baseUrl}/countries/${params.countryId}/tax-codes`;
      },
    });

    this.getAllCountries = get({
      authContext,
      url: `${baseUrl}/countries`,
    });

    this.getLineItemConfigurations = get({
      authContext,
      url: `${baseUrl}/line-item-configuration`,
    });

    this.createLineItemConfiguration = modify({
      method: 'POST',
      authContext,
      url: `${baseUrl}/line-item-configuration`,
      transformBody: transformCreateBody,
    });

    this.deleteLineItemConfiguration = modify({
      method: 'DELETE',
      authContext,
      url: (params) => {
        return `${baseUrl}/line-item-configuration/${params.id}`;
      },
    });
  }
}
